'use client'

import useFormatDate from '@/src/hooks/date/useFormatDate'
import { getFormattedDate } from '@/src/utils/date'
import dayjs from 'dayjs'
import { useLocale, useTranslations } from 'next-intl'
import { useCallback, useEffect, useState } from 'react'

import HotelDateRangeField, { type HotelDateLeg } from './hotelDateRangeField'

export interface HotelDateRangeFieldContainerProps {
    checkIn: Date | null
    checkOut: Date | null
    /** check-in + 1 day; the calendar must not offer a same-day check-out. */
    minCheckOutDate: Date
    active: boolean
    onActivate: () => void
    onClose: () => void
    onCheckInChange: (date: Date | null) => void
    onCheckOutChange: (date: Date | null) => void
}

const HotelDateRangeFieldContainer: React.FC<HotelDateRangeFieldContainerProps> = ({
    checkIn,
    checkOut,
    minCheckOutDate,
    active,
    onActivate,
    onClose,
    onCheckInChange,
    onCheckOutChange
}) => {
    const locale = useLocale()
    const t = useTranslations('common')
    const { formatDate } = useFormatDate()
    const [activeLeg, setActiveLeg] = useState<HotelDateLeg>('checkIn')
    const [today] = useState(() => dayjs().startOf('day').toDate())

    // Every fresh open starts on check-in, whichever way the popover was dismissed.
    useEffect(() => {
        if (!active) setActiveLeg('checkIn')
    }, [active])

    const formatLegValue = (date: Date | null) =>
        date ? formatDate(getFormattedDate(date, 'MM/DD/YYYY'), 'ddd, DD MMM') : t('selectDate')

    const handleLegClick = useCallback(
        (leg: HotelDateLeg) => {
            if (active && activeLeg === leg) {
                onClose()
                return
            }
            setActiveLeg(leg)
            if (!active) onActivate()
        },
        [active, activeLeg, onActivate, onClose]
    )

    const handleCalendarClick = useCallback(
        (dates: { from?: Date; to?: Date }) => {
            if (activeLeg === 'checkIn') {
                if (!dates.from) return
                onCheckInChange(dates.from)
                // Hand off within the same open popover — no close/reopen round trip.
                setActiveLeg('checkOut')
                return
            }
            if (!dates.to) return
            onCheckOutChange(dates.to)
            onClose()
        },
        [activeLeg, onCheckInChange, onCheckOutChange, onClose]
    )

    return (
        <HotelDateRangeField
            checkIn={checkIn}
            checkOut={checkOut}
            checkInLabel={t('checkIn')}
            checkOutLabel={t('checkOut')}
            checkInText={formatLegValue(checkIn)}
            checkOutText={formatLegValue(checkOut)}
            isRtl={locale === 'ar'}
            open={active}
            activeLeg={activeLeg}
            checkOutEnabled={Boolean(checkIn)}
            calendarMinDate={activeLeg === 'checkOut' ? minCheckOutDate : today}
            onLegClick={handleLegClick}
            onCalendarClick={handleCalendarClick}
        />
    )
}

export { HotelDateRangeFieldContainer as HotelDateRangeField }
export type { HotelDateLeg }
export default HotelDateRangeFieldContainer
